/**
 * UranIA PA — API client AI Gateway (US-030).
 *
 * Wrapper su `fetch` per le route della pipeline AI Gateway esposte da
 * `services/ai-gateway`:
 *   - `POST /api/v1/gateway/invocations`                           -> invocazione
 *     sincrona pipeline a 11 fasi (US-020/021/022/027 cablate in Fase 1).
 *   - `GET  /api/v1/gateway/invocations/{invocationId}/stream`     -> stream SSE
 *     dei chunk di risposta progressivi (contratto stabilito in US-030
 *     AC#3; backend deferred a Fase 2 — il client apre comunque la
 *     connessione e degrada graceful su 404/network error finche'
 *     l'endpoint non sara' deployato — coerente con il pattern adottato
 *     da `lib/admin/tenants-api.ts`).
 *
 * Boundary:
 *   - Bearer JWT preso da `useAuthStore.accessToken` (in-memory, FR-F1-10).
 *   - `credentials: 'include'` per SSO cookie Keycloak (silent renew).
 *   - Idempotency-Key UUID v4 generata client-side (AC#2 letterale)
 *     via Web Crypto API (`crypto.randomUUID()`).
 *   - Problem+JSON RFC 9457 quando 4xx/5xx; fallback su `statusText`.
 *   - Niente PII nei log: la pagina logga `tenant_id` + `invocation_id`
 *     ma mai il body utente.
 *
 * Pipeline AI Gateway (FR-F1-6 + analisi cap. 12):
 *   - Tutte le risposte includono `explain_l1` (utente) + `explain_l2`
 *     (auditor) — AgID P.6/P.7 dual-explain.
 *   - `blocked=true` quando kill switch / clarification / validation HARD
 *     fail; AC#6 letterale prevede toast dedicato per 503 (kill switch
 *     globale o servizio degradato) con `Problem.detail` letterale.
 */

import { loadSpaEnv } from '../env.ts';
import { useAuthStore } from '../auth/auth-store.ts';

export interface GatewayReasoningDto {
  readonly domain: string;
  readonly rules: readonly string[];
  readonly confidence: number;
  readonly skill_id: string | null;
  readonly canonical_form: string;
  readonly clarification_question: string | null;
}

/**
 * Fonte citata esposta dal Gateway in `GatewayResponse.sources` (US-026).
 * Allineato a `RetrievedChunkResponse` di `services/rag/openapi.yaml`
 * (US-014 AC#5): `doc_id`, `section`, `page`, `score`.  Il backend
 * cablera' la propagazione in `services/ai-gateway/routes/invocations`
 * in una US successiva: finche' il campo non e' emesso il client
 * applica `sources ?? []` -> empty state della sidebar.
 */
export interface GatewaySourceDto {
  readonly doc_id: string;
  readonly section: string;
  readonly page: number | null;
  readonly score: number;
}

export interface GatewayInvocationResponseDto {
  readonly response: string;
  readonly blocked: boolean;
  readonly question: string | null;
  readonly reasoning: GatewayReasoningDto;
  readonly provider_used: string;
  readonly latency_ms: number;
  readonly explain_l1: string;
  readonly explain_l2: string;
  readonly low_trust: boolean;
  readonly invocation_id?: string | null;
  readonly sources?: readonly GatewaySourceDto[];
}

export type DataSensitivity = 'public' | 'internal' | 'restricted';

export interface SubmitInvocationInput {
  readonly tenantId: string;
  readonly query: string;
  readonly dataSensitivity: DataSensitivity;
  /**
   * Skill hint opzionale: forza la pipeline ad usare la skill indicata
   * (sovrascrive il reasoning skill router US-059). Esempio: la sidebar
   * Agenti del workspace passa qui ``skill_verifica_gber`` quando l'utente
   * clicca la relativa card.
   */
  readonly skillHint?: string;
}

export interface GatewayApiErrorPayload {
  readonly status: number;
  readonly code: string | null;
  readonly title: string;
  readonly detail: string | null;
  readonly type: string | null;
}

export class GatewayApiError extends Error {
  public readonly status: number;
  public readonly code: string | null;
  public readonly detail: string | null;
  public readonly type: string | null;

  constructor(payload: GatewayApiErrorPayload) {
    super(payload.title);
    this.name = 'GatewayApiError';
    this.status = payload.status;
    this.code = payload.code;
    this.detail = payload.detail;
    this.type = payload.type;
  }
}

interface ProblemBody {
  readonly type?: unknown;
  readonly title?: unknown;
  readonly detail?: unknown;
  readonly status?: unknown;
  readonly code?: unknown;
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<GatewayApiError> {
  let body: ProblemBody = {};
  try {
    body = (await response.json()) as ProblemBody;
  } catch {
    // server non ha emesso Problem+JSON: fallback su statusText.
  }
  const title =
    typeof body.title === 'string' && body.title.length > 0
      ? body.title
      : response.statusText || 'http_error';
  const detail = typeof body.detail === 'string' ? body.detail : null;
  const code = typeof body.code === 'string' ? body.code : null;
  const type = typeof body.type === 'string' ? body.type : null;
  return new GatewayApiError({
    status: response.status,
    code,
    title,
    detail,
    type,
  });
}

/**
 * Genera una Idempotency-Key v4 (RFC 4122) via Web Crypto API.
 * AC US-030 #2 letterale: "Generate client-side Idempotency-Key (UUID v4)".
 */
export function generateIdempotencyKey(): string {
  // `crypto.randomUUID()` e' Web Crypto API (Secure Context). Tutti i
  // browser target del progetto (Chromium 92+, Firefox 95+, Safari 15.4+)
  // lo supportano nativamente. Fallback non necessario in produzione SPA.
  return crypto.randomUUID();
}

/**
 * POST /api/v1/gateway/invocations.
 *
 * AC#2 letterale: Idempotency-Key UUID v4 inviata come header HTTP.
 * AC#6 letterale: 503 -> Problem+JSON detail surfaced via toast (caller).
 *
 * @throws GatewayApiError quando lo status non e' 2xx o il network fallisce.
 */
export async function submitInvocation(
  input: SubmitInvocationInput,
  idempotencyKey: string,
): Promise<GatewayInvocationResponseDto> {
  const requestBody: Record<string, unknown> = {
    tenant_id: input.tenantId,
    idempotency_key: idempotencyKey,
    query: input.query,
    data_sensitivity: input.dataSensitivity,
  };
  if (typeof input.skillHint === 'string' && input.skillHint.length > 0) {
    requestBody.skill_hint = input.skillHint;
  }
  const init: RequestInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
      ...authHeader(),
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  };
  let response: Response;
  try {
    response = await fetch(buildUrl('/api/v1/gateway/invocations'), init);
  } catch (err) {
    throw new GatewayApiError({
      status: 0,
      code: 'network_error',
      title: err instanceof Error ? err.message : 'network_error',
      detail: null,
      type: null,
    });
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  return (await response.json()) as GatewayInvocationResponseDto;
}

export interface OpenInvocationStreamOptions {
  /** Callback chunk testuale ricevuto (`event: chunk`, `data: <text>`). */
  readonly onChunk: (chunk: string) => void;
  /** Callback opzionale chiamata quando lo stream si chiude lato server. */
  readonly onDone?: () => void;
  /** Callback opzionale chiamata su qualunque errore (network/parse/4xx). */
  readonly onError?: (error: Error) => void;
  /** AbortSignal per chiudere la connessione in cleanup React. */
  readonly signal: AbortSignal;
}

/**
 * GET /api/v1/gateway/invocations/{invocationId}/stream — SSE.
 *
 * AC US-030 #3 letterale: "Open SSE connection to GET /api/v1/gateway/
 * invocations/{id}/stream for streaming chunks".
 *
 * Implementazione fetch+ReadableStream (al posto di `EventSource`) per:
 *   1. Permettere `Authorization: Bearer <jwt>` (EventSource non supporta
 *      header custom; passare il token in querystring lo esporrebbe nei
 *      log proxy — anti-pattern security).
 *   2. Gestire AbortSignal per cleanup React 19 (StrictMode double-invoke).
 *   3. Parsing event-stream conforme W3C: `data:` + `event:` + delimiter
 *      doppio newline; ignora keepalive (`:` heartbeat) e linee vuote.
 *
 * Backend Fase 1: l'endpoint NON e' ancora deployato (deferred Fase 2).
 * Il client riceve 404/network error e propaga via `onError`; la UI
 * degrada graceful mostrando la risposta sincrona della POST.
 */
export async function openInvocationStream(
  invocationId: string,
  options: OpenInvocationStreamOptions,
): Promise<void> {
  const url = buildUrl(`/api/v1/gateway/invocations/${encodeURIComponent(invocationId)}/stream`);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...authHeader(),
      },
      credentials: 'include',
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return;
    }
    options.onError?.(err instanceof Error ? err : new Error('network_error'));
    return;
  }
  if (!response.ok) {
    const problem = await parseProblem(response);
    options.onError?.(problem);
    return;
  }
  const body = response.body;
  if (body === null) {
    options.onError?.(new Error('stream_body_null'));
    return;
  }
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      // Event delimiter SSE: doppio newline (LF LF o CR LF CR LF).
      let separatorIdx = buffer.indexOf('\n\n');
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx);
        buffer = buffer.slice(separatorIdx + 2);
        emitEvent(rawEvent, options);
        separatorIdx = buffer.indexOf('\n\n');
      }
    }
    if (buffer.length > 0) {
      emitEvent(buffer, options);
    }
    options.onDone?.();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return;
    }
    options.onError?.(err instanceof Error ? err : new Error('stream_read_error'));
  }
}

function emitEvent(rawEvent: string, options: OpenInvocationStreamOptions): void {
  const lines = rawEvent.split(/\r?\n/u);
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith(':')) {
      // SSE keepalive heartbeat — ignorare.
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /u, ''));
    }
  }
  if (dataLines.length === 0) {
    return;
  }
  options.onChunk(dataLines.join('\n'));
}
